import { CommonModule } from '@angular/common';
import { Component, inject, ViewChild } from '@angular/core';
import { formatISO } from 'date-fns';
import { Extension, ViewerModule } from "ng2-adsk-forge-viewer";
import { BehaviorSubject, Subscription, takeUntil } from 'rxjs';
import { ZoneShape } from 'src/app/classes/viewer/shapes/zone';
import { ZoneExtension } from 'src/app/classes/viewer/zones/extension';
import { WidgetDirective } from 'src/app/components/project-pages/dashboard/widgets/widget.directive';
import { Config } from 'src/app/interfaces/shared/config';
import { ModelService } from 'src/app/services/api/models/model.service';
import * as THREE from 'three';
import * as threeDtools from '../../../../../classes/shared/threeDTools';
import { PointShape } from '../../../../../classes/viewer/shapes/point';
import { WidgetTitleComponent } from 'src/app/components/project-pages/dashboard/widget-title/widget-title.component';
import { WidgetLoadingComponent } from 'src/app/components/project-pages/dashboard/widget-loading/widget-loading.component';

declare var Autodesk: any;

@Component({
  selector: 'app-forge',
  imports: [
    WidgetLoadingComponent,
    WidgetTitleComponent,
    CommonModule,
    ViewerModule
  ],
  standalone: true,
  templateUrl: './forge.component.html',
  styleUrl: './forge.component.scss'
})

export class ForgeComponent extends WidgetDirective {
  // Services specific for ForgeComponent
  private readonly modelService: ModelService = inject(ModelService);

  // Component API specific for ForgeComponent
  @ViewChild('forgeContent', { static: false }) forgeContent: any;

  // Variables specific for ForgeComponent
  containerStyle: string = "";
  blockStyle: string = "width:100%;display:flex;flex-direction: column;";
  titleStyle: string = "font-size:16px;margin:0px 20px 20px;";
  chartData: any;

  model: any;
  viewerOptions: any;
  viewer: any;
  currentTask: any;
  currentMetric: any;
  planningData$: BehaviorSubject<any> = new BehaviorSubject(null);
  tasks$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  metrics$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  fragments$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  currentTask$: BehaviorSubject<any> = new BehaviorSubject(null);
  currentMetric$: BehaviorSubject<any> = new BehaviorSubject(null);
  selectedFragments$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  selectedMetrics$: BehaviorSubject<string[]> = new BehaviorSubject<any[]>([]);
  selectedTasks$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  currentDate$: BehaviorSubject<string> = new BehaviorSubject(formatISO(new Date()));
  currentDay$: BehaviorSubject<string> = new BehaviorSubject("");
  startDate?: string;
  endDate?: string;
  planning: any;
  metrics: any;
  fragments: any[] | undefined;
  planningLoading = false;
  days: any[] | undefined;
  chartRefresh?: Subscription;
  chartExport?: Subscription;
  chartIsReady: boolean = false;
  chartRendered: boolean = false;
  theme: string = "light";

  init() {
    if (!this.widget || !this.widget.visible || this.resize) return

    this.widgetDesign = this.widget.design;
    this.chartData = this.widget.data;

    if (this.type !== this.widget.type) {
      this.resetStyles();
    }

    this.type = this.widget.type;
    this.initOptions();

    if (!this.chartIsReady) {
      setTimeout(() => this.withContent(() => {
        this.initForge();
        this.updateForge();
        this.chartRendered = true;
      }), 200)
    } else {
      this.updateForge();
      this.chartRendered = true;
    }
  }

  resetStyles() {
    this.blockStyle = "width:100%;display:flex;flex-direction: column;align-content:center;";
    this.titleStyle = "font-size:16px;margin:0px 20px 20px;";
    this.containerStyle = "";
  }

  initOptions() {
    try {
      this.resetStyles();
      let options = this.widgetDesign;
      if (!options) return

      this.containerStyle = `background-color:${this.theme == "light" ? "white" : "#333"};`
      this.blockStyle += `height:100%;color:${this.theme == "light" ? "black" : "white"};text-align:'start';`;

    } catch (e) {
      console.error(e)
    }
  }

  initForge() {
    if (!this.widget.content) return console.log("Can't find Model")

    this.model = this.widget.content.model;
    this.viewerOptions = this.getViewerOptions();
    this.viewerOptions.onViewerInitialized = (args: { viewer: any; viewerComponent: { DocumentId: any; }; }) => {

      this.viewer = args.viewer;

      Object.keys(Config.colors).forEach(c => {
        try {
          let material: any = new THREE.MeshPhongMaterial({
            color: Config.colors[c],
            transparent: true, opacity: 0.5
          });

          this.viewer.impl.createOverlayScene("zone_" + c, material);
        } catch (e) {
          console.error(`[ForgeComponent] Overlay error:`, e);
        }
      });

      let pointMaterial: any = new THREE.MeshPhongMaterial({
        color: "#0066ff",
        transparent: true, opacity: 0.6
      });

      this.viewer.impl.createOverlayScene("point", pointMaterial);
      args.viewerComponent.DocumentId = this.model.urn;

      this.viewer.addEventListener(Autodesk.Viewing.TOOLBAR_CREATED_EVENT, () => {
        this.updateForge()
      })
    }

    if (this.model.origin.position.lat && this.model.origin.position.lon) {
      this.model.origin.projectLatLonXY = threeDtools.getXYFromLatLonAlt2(this.model.origin.position.lat, this.model.origin.position.lon, 0);
    } else {
      this.model.origin.projectLatLonXY = [0, 0]
    }
    if (this.model.origin.position.x != undefined && this.model.origin.position.y != undefined) {
      this.model.origin.projectXY = [parseFloat(this.model.origin.position.x), parseFloat(this.model.origin.position.y)];
    } else {
      this.model.origin.projectXY = [0, 0]
    }
    this.model.offset = {
      x: 0,
      y: 0,
      z: 0
    }
    this.chartIsReady = true;
  }

  setForgePoint(point: any) {
    let pointObj = new PointShape(point);
    this.viewer.impl.addOverlay("point", pointObj.mesh);
    this.viewer.points.push(pointObj)
  }


  setForgeZone(zone: any) {
    let zoneObj = new ZoneShape(zone);

    if (zone.geometry && zone.geometry.position && zone.geometry.position.length > 2) {
      zoneObj.drawGeometry(true);
    } else if (zone.polygon && zone.polygon.length > 2) {
      zoneObj.draw(true);
    }

    this.viewer.zones.push(zoneObj)
  }

  getViewerOptions() {
    return {
      initializerOptions: {
        env: "AutodeskProduction",
        getAccessToken: (onGetAccessToken: (token: string, expire: number) => void) => {
          this.modelService.getToken().pipe(takeUntil(this.destroy$)).subscribe((res: { access_token: string; expires_in: number; }) => {
            onGetAccessToken(res.access_token, res.expires_in);
          })
        },
        api: "derivativeV2",
        enableMemoryManagement: true
      },
      viewerConfig: {
        extensions: [ZoneExtension.extensionName],
        theme: this.theme == "light" ? "bim-theme" : "dark-theme"
      },
      onViewerScriptsLoaded: () => {
        Extension.registerExtension(ZoneExtension.extensionName, ZoneExtension);
      },
      onViewerInitialized: (args: { viewer: any; viewerComponent: { DocumentId: any; }; }) => {
        this.widget.content.viewer = args.viewer;
        args.viewerComponent.DocumentId = this.model.urn;
      }
    };
  }

  updateForge() {
    let pointList = [];
    let options = this.widgetDesign.forge;

    if (!this.viewer) return;
    if (!this.viewer.zones) this.viewer.zones = [];

    this.viewer.zones.forEach((z: { removeGeom: () => void; remove: () => void; }) => {
      z.removeGeom();
      z.remove();
    })

    if (!this.viewer.points) this.viewer.points = [];

    this.viewer.points.forEach((p: { mesh: { visible: boolean; }; }) => {
      if (p.mesh) {
        p.mesh.visible = false;
        this.viewer.impl.removeOverlay("point", p.mesh);
      }
    })

    this.viewer.impl.invalidate(true, true, true);

    if (options?.showZones && this.widget.content?.zones) {
      this.widget.content.zones.forEach((z: any) => {
        z.viewer = this.viewer;
        z.model = this.model;
        this.setForgeZone(z);
      })
    }

    if (Array.isArray(this.chartData))
      (this.chartData as Array<{ x: number, y: number, z?: number, s?: string, scale?: number }>).forEach((d, i) => {
        if (d.x && d.y && d.x > -180 && d.x < 180) {

          if (Array.isArray(d.y)) d.y = d.y[0];
          if (isNaN(d.y) || d.y < -90 || d.y > 90) return;

          let { x, y, z } = threeDtools.applyTransform2({ x: d.x, y: d.y, z: d.z ?? 0, lon: undefined, lat: undefined }, this.model.origin, this.model.offset);
          let point: any = {};
          point.x = x;
          point.y = y;
          point.z = z;
          point.scale = this.model.origin.scale;
          this.setForgePoint(point);

          pointList.push({ lon: d.x, lat: d.y, z: d.z || this.model.origin.position.z, scale: d.scale || 1, color: d.s });
        }

        return;
      });

    this.viewer.impl.invalidate(true, true, true);
    this.viewer.impl.sceneUpdated(true);
  }
}
